import React from 'react';
import { SegmentedControl } from '@tranquility-ai/theme-components/segmented-control';
import '@tranquility-ai/theme-components/segmented-control.css';
import { TpTooltip } from '@tranquility-ai/theme-components/tooltip';
import { useTimePilotT } from '@/i18n/useTimePilotT';

export type CaptureViewSwitcherProps = {
  viewCapture: boolean;
  hasSavedCapture: boolean;
  onChange: (viewCapture: boolean) => void;
  /** When true, neither segment is selectable (capture mode is active). */
  disabled?: boolean;
};

/**
 * Full Media / Thumbnail segment control for the media toolbar — matches the
 * Obfuscated / Original switcher and other dense-toolbar segmented controls.
 * Named "Full Media" (not "Original") so it is not confused with CSAM preview.
 */
export function CaptureViewSwitcher({
  viewCapture,
  hasSavedCapture,
  onChange,
  disabled = false,
}: CaptureViewSwitcherProps) {
  const t = useTimePilotT();
  const value = hasSavedCapture && viewCapture ? 'capture' : 'original';

  return (
    <TpTooltip
      content={viewCapture ? t('media.viewFull') : t('media.viewCapture')}
      placement="bottom"
    >
      <SegmentedControl
        aria-label={t('media.mediaView')}
        size="sm"
        variant="dark"
        value={value}
        onValueChange={(next) => {
          if (disabled) return;
          onChange(next === 'capture');
        }}
        options={[
          { value: 'original', label: t('media.fullMedia'), disabled },
          {
            value: 'capture',
            label: t('media.capture'),
            disabled: disabled || !hasSavedCapture,
          },
        ]}
      />
    </TpTooltip>
  );
}
